import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { Tone } from './state.ts';
import { midEllipsis } from './format.ts';
import styles from './StatusMatrix.module.css';

/* How many characters of a column id survive in the rotated label track.
   `--w-collabel` (68px) at --fs-micro mono fits ~11; anything longer is
   middle-ellipsed so the discriminating END is what stays on screen —
   `jetrover-01`, never eleven identical columns reading `jetrover…`. */
const COL_LABEL_CHARS = 11;

/* ==========================================================================
   StatusMatrix — metric rows × entity columns.

   THE BAR §8 tell #11: "a scrolling list of 150 robot cards is the single
   biggest failure available to us." The answer is this: abandon row-per-
   device at fleet scale and read health as *texture*. Six metrics over 28
   robots is 168 live readings in ~114px — and you only read a name where the
   texture breaks.

   Interaction is the other half. Hovering a column lights it and swaps the
   caption line for a full readout of that robot; clicking opens it. Arrow
   keys walk the grid with a roving tabindex, so the whole fleet is reachable
   from the keyboard without 168 tab stops.
   ========================================================================== */

export interface MatrixReading {
  tone: Tone;
  /** The value in words — what the readout and the screen reader say. */
  text: string;
  /** Nothing is arriving. Drawn as an outline, never as a filled colour. */
  hollow?: boolean | undefined;
}

export interface MatrixMetric<T> {
  key: string;
  label: string;
  /** Return null for "no reading". A missing value never renders healthy. */
  read: (item: T) => MatrixReading | null;
}

interface Props<T> {
  items: readonly T[];
  itemKey: (item: T) => string;
  /** Short identity for the readout and the column label — the robot id. */
  itemLabel: (item: T) => string;
  metrics: readonly MatrixMetric<T>[];
  /** Marks the entity currently open elsewhere on the screen. */
  currentKey?: string | null | undefined;
  onPick?: ((item: T) => void) | undefined;
  /** Rotated column ids under the grid. Drop them past ~60 columns. */
  showLabels?: boolean | undefined;
  /** Left-hand caption while nothing is hovered. Keeps the height stable. */
  caption?: ReactNode | undefined;
  /** Right-hand tone key: `<MatrixKey items={[…]} />`. */
  legend?: ReactNode | undefined;
}

export function StatusMatrix<T>({
  items,
  itemKey,
  itemLabel,
  metrics,
  currentKey,
  onPick,
  showLabels = true,
  caption,
  legend,
}: Props<T>) {
  const [hover, setHover] = useState<number | null>(null);
  const [active, setActive] = useState<[number, number]>([0, 0]);
  const gridRef = useRef<HTMLDivElement | null>(null);

  const focusCell = (r: number, c: number) => {
    const rr = Math.max(0, Math.min(metrics.length - 1, r));
    const cc = Math.max(0, Math.min(items.length - 1, c));
    setActive([rr, cc]);
    const el = gridRef.current?.querySelector<HTMLElement>(`[data-cell="${rr}:${cc}"]`);
    el?.focus();
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>, r: number, c: number) => {
    const map: Record<string, [number, number]> = {
      ArrowRight: [r, c + 1],
      ArrowLeft: [r, c - 1],
      ArrowDown: [r + 1, c],
      ArrowUp: [r - 1, c],
      Home: [r, 0],
      End: [r, items.length - 1],
    };
    const next = map[e.key];
    if (next) {
      e.preventDefault();
      focusCell(next[0], next[1]);
      setHover(Math.max(0, Math.min(items.length - 1, next[1])));
      return;
    }
    if ((e.key === 'Enter' || e.key === ' ') && onPick) {
      e.preventDefault();
      const it = items[c];
      if (it !== undefined) onPick(it);
    }
  };

  const focused = hover;
  const focusedItem = focused !== null ? items[focused] : undefined;

  return (
    <div className={styles.matrix}>
      {/* Caption line. Idle it states the shape of the data; on hover it
          becomes the readout for one robot. Same height either way. */}
      <div className={styles.caption}>
        {focusedItem === undefined ? (
          <>
            <span className={styles.captionIdle}>{caption}</span>
            <span className={styles.spacer} />
            {legend}
          </>
        ) : (
          <>
            <span className={styles.readName}>{itemLabel(focusedItem)}</span>
            <span className={styles.readList}>
              {metrics.map((m) => {
                const v = m.read(focusedItem);
                return (
                  <span key={m.key} className={styles.readItem}>
                    <span className={styles.readKey}>{m.label}</span>
                    <span
                      className={styles.readVal}
                      data-tone={v?.tone ?? 'neutral'}
                      data-known={v !== null}
                    >
                      {v?.text ?? '—'}
                    </span>
                  </span>
                );
              })}
            </span>
          </>
        )}
      </div>

      <div className={styles.body}>
        <div className={styles.rowLabels}>
          {metrics.map((m) => (
            <div key={m.key} className={styles.rowLabel}>{m.label}</div>
          ))}
          {showLabels && <div className={styles.rowLabelPad} />}
        </div>

        <div
          className={styles.gridScroll}
          ref={gridRef}
          role="grid"
          aria-label="Fleet status matrix"
          aria-rowcount={metrics.length}
          aria-colcount={items.length}
          onMouseLeave={() => setHover(null)}
        >
          <div className={styles.grid}>
            {metrics.map((m, r) => (
              <div key={m.key} className={styles.row} role="row">
                {items.map((it, c) => {
                  const v = m.read(it);
                  const k = itemKey(it);
                  const label = `${itemLabel(it)} — ${m.label} ${v?.text ?? 'no reading'}`;
                  return (
                    <div
                      key={k}
                      role="gridcell"
                      className={styles.cellBox}
                      data-cell={`${r}:${c}`}
                      data-hot={hover === c}
                      data-current={currentKey != null && currentKey === k}
                      tabIndex={active[0] === r && active[1] === c ? 0 : -1}
                      aria-label={label}
                      title={label}
                      onMouseEnter={() => setHover(c)}
                      onFocus={() => { setActive([r, c]); setHover(c); }}
                      onKeyDown={(e) => onKeyDown(e, r, c)}
                      onClick={onPick === undefined ? undefined : () => onPick(it)}
                    >
                      <span
                        className={styles.cell}
                        data-tone={v?.tone ?? 'neutral'}
                        data-hollow={v === null || v.hollow === true}
                      />
                    </div>
                  );
                })}
              </div>
            ))}

            {showLabels && (
              <div className={styles.labelRow} aria-hidden="true">
                {items.map((it, c) => (
                  <div
                    key={itemKey(it)}
                    className={styles.colLabel}
                    data-hot={hover === c}
                    data-current={currentKey != null && currentKey === itemKey(it)}
                    onMouseEnter={() => setHover(c)}
                  >
                    <span title={itemLabel(it)}>{midEllipsis(itemLabel(it), COL_LABEL_CHARS)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The tone key for a matrix. Dots and words — never a row of filled pills. */
export function MatrixKey({
  items,
}: {
  items: readonly { tone: Tone; label: string; hollow?: boolean | undefined }[];
}) {
  return (
    <span className={styles.key}>
      {items.map((i) => (
        <span key={i.label} className={styles.keyItem}>
          <span className={styles.keySwatch} data-tone={i.tone} data-hollow={i.hollow === true} />
          {i.label}
        </span>
      ))}
    </span>
  );
}
